import { useState } from 'react';
import { Package } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ProductImage component - handles all product image display with proper fallbacks
 * Automatically constructs full URL and handles loading/error states
 */
export default function ProductImage({ 
  src, 
  alt = "Product", 
  className = "w-full h-full object-cover",
  fallbackClassName = "w-full h-full flex items-center justify-center bg-gradient-to-br from-[#121212] to-[#0A0A0A]",
  iconSize = "w-10 h-10"
}) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Construct full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // If already a full URL, use as-is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // If it's a relative path, prepend backend URL
    return `${BACKEND_URL}${imagePath.startsWith('/') ? imagePath : '/' + imagePath}`;
  };

  const imageUrl = getImageUrl(src);

  // No image source provided
  if (!imageUrl || imageError) {
    return (
      <div className={fallbackClassName}>
        <Package className={`${iconSize} text-[#FFD700]/30`} />
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      {/* Loading placeholder - shows while image is loading */}
      {!imageLoaded && (
        <div className={`absolute inset-0 ${fallbackClassName} animate-pulse`}>
          <Package className={`${iconSize} text-[#FFD700]/20`} />
        </div>
      )}
      
      {/* Actual image */}
      <img
        src={imageUrl}
        alt={alt}
        className={`${className} ${imageLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
        onLoad={() => setImageLoaded(true)}
        onError={() => {
          setImageError(true);
          setImageLoaded(true);
        }}
        loading="lazy"
        style={{ objectFit: 'cover' }}
      />
    </div>
  );
}

/**
 * Simple image URL helper for use in other components
 */
export const getFullImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  return `${BACKEND_URL}${imagePath.startsWith('/') ? imagePath : '/' + imagePath}`;
};
